import {
  CopyOutlined,
  InfoCircleFilled,
  SyncOutlined,
} from '@ant-design/icons';
import { Alert, Button, Form, Input, Layout } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { SCard, SDetailLayout } from 'app/components/StaycationUI';
import {
  CustomRow,
  InputTextAreaRow,
} from 'app/components/commons/CustomFields/CustomFields';
import { ConfigContext } from 'app/context/ConfigContext/ConfigContext';
import { useSEOLanding } from 'app/hooks/data/seoLandings/useSEOLanding';
import { useUpdateSEOLanding } from 'app/hooks/data/seoLandings/useUpdateSEOLanding';
import { useCopyToClipboard } from 'app/utils/clipboard';

import { StatusBadge } from '../SEOLandings';

import './SEOLandingDetail.scss';
import { UnpublishedModal } from './_components/UnpublishedModal/UnpublishedModal';

type Props = {
  match: {
    params: {
      slug: string;
    };
  };
};

const ALERT_TIMEOUT = 3000;

type AlertState =
  | { type: 'success'; message: string }
  | { type: 'error'; message: string }
  | null;

type AlertProps = {
  type: 'success' | 'error';
  message: string;
};

export const SEOLandingsDetail = ({ match }: Props) => {
  const { copied, copyToClipboardWithAlert } = useCopyToClipboard();
  const [alertState, setAlertState] = useState<AlertState>(null);

  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const landingSlug = decodeURIComponent(match.params?.slug);
  const { mutate: updateLanding } = useUpdateSEOLanding();

  const { data: landing, isFetching } = useSEOLanding(landingSlug);
  const [form] = Form.useForm();
  const config = useContext(ConfigContext);
  const redirectUrl = Form.useWatch('redirectionUrl', form);
  const status = Form.useWatch('status', form);
  const content = Form.useWatch('content', form);

  const isPublished = status === 'published';

  const alertTimeoutRef = useRef<number | null>(null);

  const updateAlertState = (state: AlertProps) => {
    setAlertState(state);

    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current);
    }

    setTimeout(() => {
      setAlertState(null);
      alertTimeoutRef.current = null;
    }, ALERT_TIMEOUT);
  };

  const initialValues = useMemo(
    () => ({
      url: `${config?.appUrl}${landingSlug}`,
      redirectionUrl: landing?.redirectionUrl ?? '',
      content: landing?.content?.length
        ? JSON.stringify(landing.content[0], null, 2)
        : '',
      status: landing?.status,
    }),
    [landing, config?.appUrl, landingSlug]
  );

  useEffect(() => {
    form.resetFields();
  }, [form, initialValues]);

  if (!landing || isFetching) {
    return null;
  }

  const isDirty = content !== initialValues.content;

  const handleSubmit = ({ publish }: { publish: boolean }) => {
    let parsedContent;

    try {
      parsedContent = JSON.parse(content.toString());
    } catch {
      updateAlertState({
        type: 'error',
        message: 'Content format is invalid. Please provide valid JSON.',
      });

      return;
    }

    try {
      updateLanding(
        {
          slug: landingSlug,
          payload: {
            redirectionUrl: publish ? '' : redirectUrl,
            content: [parsedContent],
            status: publish ? 'published' : 'unpublished',
          },
        },
        {
          onSuccess: () => {
            updateAlertState({
              type: 'success',
              message: publish
                ? 'Landing successfully updated'
                : 'Landing successfully unpublished',
            });
          },
          onError: () => {
            updateAlertState({
              type: 'error',
              message: 'An error occurred while updating the landing',
            });
          },
        }
      );
    } catch (error) {
      updateAlertState({
        type: 'error',
        message: 'An error occurred while updating the landing',
      });
      console.error('Error submitting form:', error);
    }
  };

  const isSubmitDisabled = isPublished && (!isDirty || !content?.trim());

  const title = landingSlug.startsWith('/')
    ? landingSlug.slice(1)
    : landingSlug;

  return (
    <>
      <Layout className="landing-detail-layout">
        {copied && (
          <Alert
            className="alert"
            type="success"
            message="URL successfully copied"
            icon={<InfoCircleFilled />}
            showIcon
          />
        )}
        {alertState && (
          <Alert
            className="alert"
            type={alertState.type}
            message={alertState.message}
            showIcon
            onClose={() => setAlertState(null)}
          />
        )}

        <SDetailLayout
          mode="edit"
          isDirty={isDirty}
          title={title}
          backRoute="/seo-landings"
          customFooterButtons={
            <div className="sticky-footer">
              <div className="buttons">
                {isPublished && (
                  <Button
                    type="ghost"
                    size="large"
                    onClick={() => setIsDetailModalOpen(true)}
                  >
                    Unpublish
                  </Button>
                )}
                <Button
                  type="primary"
                  size="large"
                  onClick={() => {
                    handleSubmit({ publish: true });
                  }}
                  disabled={isSubmitDisabled}
                >
                  {isPublished ? 'Save' : 'Publish'}
                </Button>
              </div>
            </div>
          }
        >
          <Content>
            <Form
              form={form}
              initialValues={initialValues}
              onFinish={handleSubmit}
            >
              <div className="page-content">
                <SCard title="Details">
                  <CustomRow label="Status" name="status" required>
                    {StatusBadge(status)}
                  </CustomRow>

                  <div
                    onClick={(e) =>
                      copyToClipboardWithAlert(
                        e,
                        `${config?.appUrl}${landingSlug}`
                      )
                    }
                  >
                    <CustomRow label="URL" name="url">
                      <Input
                        readOnly
                        addonAfter={<CopyOutlined className="copy-icon" />}
                      />
                    </CustomRow>
                  </div>

                  {!isPublished && (
                    <CustomRow label="Redirection URL" name="redirectionUrl">
                      <Input
                        addonAfter={
                          <CopyOutlined
                            className="copy-icon"
                            onClick={(e) =>
                              copyToClipboardWithAlert(e, redirectUrl)
                            }
                          />
                        }
                      />
                    </CustomRow>
                  )}
                  <InputTextAreaRow
                    label="Content"
                    name="content"
                    required
                    rows={12}
                  />
                </SCard>
              </div>
            </Form>
          </Content>
        </SDetailLayout>
      </Layout>
      <UnpublishedModal
        isDetailModalOpen={isDetailModalOpen}
        setIsDetailModalOpen={setIsDetailModalOpen}
        title="Unpublish SEO Landing"
        isDisabled={!redirectUrl?.trim()}
        form={form}
        handleSubmit={handleSubmit}
      >
        <div className="container-modal">
          <div className="details-modal-container">
            <p className="title-modal">
              <SyncOutlined className="icon-modal" />
              Redirect URL
            </p>
            <p>
              To confirm the unpublication of this SEO landing page, you must
              define a redirect URL where users will be guided instead.
            </p>
          </div>
          <Form.Item
            noStyle
            name="redirectionUrl"
            rules={[{ required: true, message: 'Redirect URL is required' }]}
          >
            <Input placeholder="Enter a valid URL here" />
          </Form.Item>
        </div>
      </UnpublishedModal>
    </>
  );
};
